import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { API_ENDPOINTS } from '../../config/apiRouting';
import { SocialMediaConfiguration } from '../social-media/SocialMediaConfiguration';
import { SocialMediaPostManager } from '../social-media/SocialMediaPostManager';
import { LinkedInBusiness } from '../social-media/LinkedInBusiness';
import { FacebookOAuth } from '../social-media/FacebookOAuth';
import { GoogleBusiness } from '../social-media/GoogleBusiness';
import { OfferDistribution } from '../social-media/OfferDistribution';
import { ReviewManagement } from '../social-media/ReviewManagement';
import { ContentHub } from './ContentHub';
import { ABMDashboard } from './ABMDashboard';
import './CRMDashboard.css';

interface Activity {
  id: string;
  type: 'call' | 'email' | 'demo' | 'note';
  description: string;
  createdBy: string;
  createdAt: string;
}

interface Contact {
  contactId: string;
  name: string;
  company: string;
  email: string;
  phone: string;
  status: string;
  source: string;
  notes: string;
  activities: Activity[];
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

interface Stats {
  total: number;
  byStatus: Record<string, number>;
  conversionRate: number;
  activeDeals: number;
}

const STATUS_LABELS: Record<string, string> = {
  lead: 'Lead',
  contacted: 'Contacted',
  demo_scheduled: 'Demo Scheduled',
  negotiation: 'Negotiation',
  closed_won: 'Closed Won',
  closed_lost: 'Closed Lost',
};

const PIPELINE_COLUMNS = ['lead', 'contacted', 'demo_scheduled', 'negotiation', 'closed_won', 'closed_lost'];

const SOURCES = ['manual', 'website', 'referral', 'linkedin', 'cold_outreach', 'event', 'inbound'];

const ACTIVITY_ICONS: Record<string, string> = {
  call: 'C',
  email: 'E',
  demo: 'D',
  note: 'N',
};

const emptyForm: Omit<Contact, 'contactId' | 'activities' | 'createdBy' | 'createdAt' | 'updatedAt'> = {
  name: '',
  company: '',
  email: '',
  phone: '',
  status: 'lead',
  source: 'manual',
  notes: '',
};

const BSO_STORE_ID = 'bso-platform';
const BSO_STORE_NAME = 'BigSystemOne';

type SocialSubTab = 'accounts' | 'posts' | 'linkedin' | 'facebook' | 'google' | 'offers' | 'reviews';

export const CRMDashboard: React.FC = () => {
  const { getIdToken } = useAuth();
  const [tab, setTab] = useState<'pipeline' | 'contacts' | 'add' | 'activity' | 'stats' | 'social' | 'content' | 'abm'>('pipeline');
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortField, setSortField] = useState<'name' | 'company' | 'updatedAt'>('updatedAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [activityType, setActivityType] = useState<'call' | 'email' | 'demo' | 'note'>('note');
  const [activityDesc, setActivityDesc] = useState('');

  // Social media state
  const [socialSubTab, setSocialSubTab] = useState<SocialSubTab>('accounts');

  const authHeaders = useCallback(async () => {
    const token = await getIdToken();
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };
  }, [getIdToken]);

  const fetchContacts = useCallback(async () => {
    try {
      const headers = await authHeaders();
      const res = await fetch(API_ENDPOINTS.crmContacts(), { headers });
      const data = await res.json();
      if (data.success) setContacts(data.contacts || []);
    } catch (err) {
      console.error('Failed to fetch CRM contacts:', err);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  const fetchStats = useCallback(async () => {
    try {
      const headers = await authHeaders();
      const res = await fetch(API_ENDPOINTS.crmStats(), { headers });
      const data = await res.json();
      if (data.success) setStats(data.stats);
    } catch (err) {
      console.error('Failed to fetch CRM stats:', err);
    }
  }, [authHeaders]);

  useEffect(() => {
    fetchContacts();
    fetchStats();
  }, [fetchContacts, fetchStats]);

  const handleSave = async () => {
    try {
      const headers = await authHeaders();
      if (editingId) {
        await fetch(API_ENDPOINTS.crmContact(editingId), {
          method: 'PUT',
          headers,
          body: JSON.stringify(form),
        });
      } else {
        await fetch(API_ENDPOINTS.crmContacts(), {
          method: 'POST',
          headers,
          body: JSON.stringify(form),
        });
      }
      setForm(emptyForm);
      setEditingId(null);
      setTab('contacts');
      fetchContacts();
      fetchStats();
    } catch (err) {
      console.error('Failed to save contact:', err);
    }
  };

  const handleDelete = async (contactId: string) => {
    if (!window.confirm('Delete this contact?')) return;
    try {
      const headers = await authHeaders();
      await fetch(API_ENDPOINTS.crmContact(contactId), { method: 'DELETE', headers });
      fetchContacts();
      fetchStats();
      if (selectedContact?.contactId === contactId) setSelectedContact(null);
    } catch (err) {
      console.error('Failed to delete contact:', err);
    }
  };

  const handleStatusChange = async (contactId: string, newStatus: string) => {
    try {
      const headers = await authHeaders();
      await fetch(API_ENDPOINTS.crmContact(contactId), {
        method: 'PUT',
        headers,
        body: JSON.stringify({ status: newStatus }),
      });
      fetchContacts();
      fetchStats();
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  };

  const handleLogActivity = async () => {
    if (!selectedContact || !activityDesc.trim()) return;
    try {
      const headers = await authHeaders();
      await fetch(API_ENDPOINTS.crmContactActivity(selectedContact.contactId), {
        method: 'POST',
        headers,
        body: JSON.stringify({ type: activityType, description: activityDesc }),
      });
      setActivityDesc('');
      fetchContacts();
      // Refresh selected contact
      const res = await fetch(API_ENDPOINTS.crmContacts(), { headers });
      const data = await res.json();
      if (data.success) {
        setContacts(data.contacts || []);
        const updated = (data.contacts || []).find((c: Contact) => c.contactId === selectedContact.contactId);
        if (updated) setSelectedContact(updated);
      }
    } catch (err) {
      console.error('Failed to log activity:', err);
    }
  };

  const startEdit = (contact: Contact) => {
    setForm({
      name: contact.name,
      company: contact.company,
      email: contact.email,
      phone: contact.phone,
      status: contact.status,
      source: contact.source,
      notes: contact.notes,
    });
    setEditingId(contact.contactId);
    setTab('add');
  };

  const filteredContacts = contacts
    .filter(c => {
      if (filterStatus !== 'all' && c.status !== filterStatus) return false;
      if (search) {
        const s = search.toLowerCase();
        return c.name.toLowerCase().includes(s) ||
          c.company.toLowerCase().includes(s) ||
          c.email.toLowerCase().includes(s);
      }
      return true;
    })
    .sort((a, b) => {
      const av = sortField === 'updatedAt' ? a.updatedAt || a.createdAt : (a as any)[sortField] || '';
      const bv = sortField === 'updatedAt' ? b.updatedAt || b.createdAt : (b as any)[sortField] || '';
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });

  const contactsByStatus = (status: string) => contacts.filter(c => c.status === status);

  const formatDate = (iso: string) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const toggleSort = (field: 'name' | 'company' | 'updatedAt') => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  if (loading) return <div className="crm-loading">Loading CRM...</div>;

  return (
    <div className="crm-dashboard">
      <div className="crm-tabs">
        <button className={`crm-tab ${tab === 'pipeline' ? 'active' : ''}`} onClick={() => setTab('pipeline')}>Pipeline</button>
        <button className={`crm-tab ${tab === 'contacts' ? 'active' : ''}`} onClick={() => setTab('contacts')}>Contacts</button>
        <button className={`crm-tab ${tab === 'add' ? 'active' : ''}`} onClick={() => { setTab('add'); setEditingId(null); setForm(emptyForm); }}>
          {editingId ? 'Edit Contact' : 'Add Contact'}
        </button>
        <button className={`crm-tab ${tab === 'activity' ? 'active' : ''}`} onClick={() => setTab('activity')}>Activity Log</button>
        <button className={`crm-tab ${tab === 'stats' ? 'active' : ''}`} onClick={() => { setTab('stats'); fetchStats(); }}>Stats</button>
        <button className={`crm-tab ${tab === 'social' ? 'active' : ''}`} onClick={() => setTab('social')}>Social Media</button>
        <button className={`crm-tab ${tab === 'content' ? 'active' : ''}`} onClick={() => setTab('content')}>Content</button>
        <button className={`crm-tab ${tab === 'abm' ? 'active' : ''}`} onClick={() => setTab('abm')}>ABM</button>
      </div>

      {/* Stats Summary - always visible */}
      {stats && (
        <div className="crm-stats-grid">
          <div className="crm-stat-card">
            <h4>Total Contacts</h4>
            <div className="stat-value">{stats.total}</div>
          </div>
          <div className="crm-stat-card">
            <h4>Active Deals</h4>
            <div className="stat-value">{stats.activeDeals}</div>
          </div>
          <div className="crm-stat-card">
            <h4>Closed Won</h4>
            <div className="stat-value">{stats.byStatus?.closed_won || 0}</div>
          </div>
          <div className="crm-stat-card">
            <h4>Conversion Rate</h4>
            <div className="stat-value">{stats.conversionRate}%</div>
          </div>
        </div>
      )}

      {/* Pipeline Tab */}
      {tab === 'pipeline' && (
        <div className="crm-pipeline">
          {PIPELINE_COLUMNS.map(status => {
            const items = contactsByStatus(status);
            return (
              <div key={status} className="crm-pipeline-column">
                <h3>
                  {STATUS_LABELS[status]}
                  <span className="count">{items.length}</span>
                </h3>
                {items.map(c => (
                  <div
                    key={c.contactId}
                    className="crm-pipeline-card"
                    onClick={() => { setSelectedContact(c); setTab('activity'); }}
                  >
                    <div className="card-name">{c.name}</div>
                    <div className="card-company">{c.company}</div>
                    <div className="card-meta">
                      <span>{c.source}</span>
                      <span>{formatDate(c.updatedAt)}</span>
                    </div>
                  </div>
                ))}
                {items.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '16px', color: '#94a3b8', fontSize: '13px' }}>
                    No contacts
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Contacts Tab */}
      {tab === 'contacts' && (
        <>
          <div className="crm-contacts-controls">
            <input
              type="text"
              className="crm-search-input"
              placeholder="Search contacts..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <select className="crm-filter-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
              <option value="all">All Statuses</option>
              {PIPELINE_COLUMNS.map(s => (
                <option key={s} value={s}>{STATUS_LABELS[s]}</option>
              ))}
            </select>
            <button className="crm-btn crm-btn-primary" onClick={() => { setTab('add'); setEditingId(null); setForm(emptyForm); }}>
              Add Contact
            </button>
          </div>

          {filteredContacts.length === 0 ? (
            <div className="crm-empty">
              <p>No contacts found</p>
              <button className="crm-btn crm-btn-primary" onClick={() => setTab('add')}>Add your first contact</button>
            </div>
          ) : (
            <table className="crm-contacts-table">
              <thead>
                <tr>
                  <th onClick={() => toggleSort('name')}>Name {sortField === 'name' ? (sortDir === 'asc' ? '▲' : '▼') : ''}</th>
                  <th onClick={() => toggleSort('company')}>Company {sortField === 'company' ? (sortDir === 'asc' ? '▲' : '▼') : ''}</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Status</th>
                  <th>Source</th>
                  <th onClick={() => toggleSort('updatedAt')}>Last Updated {sortField === 'updatedAt' ? (sortDir === 'asc' ? '▲' : '▼') : ''}</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredContacts.map(c => (
                  <tr key={c.contactId}>
                    <td>{c.name}</td>
                    <td>{c.company}</td>
                    <td>{c.email}</td>
                    <td>{c.phone}</td>
                    <td>
                      <select
                        className="crm-status-select"
                        value={c.status}
                        onChange={e => handleStatusChange(c.contactId, e.target.value)}
                      >
                        {PIPELINE_COLUMNS.map(s => (
                          <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                        ))}
                      </select>
                    </td>
                    <td>{c.source}</td>
                    <td>{formatDate(c.updatedAt)}</td>
                    <td>
                      <div className="crm-table-actions">
                        <button className="crm-btn crm-btn-secondary crm-btn-sm" onClick={() => { setSelectedContact(c); setTab('activity'); }}>
                          Log
                        </button>
                        <button className="crm-btn crm-btn-secondary crm-btn-sm" onClick={() => startEdit(c)}>
                          Edit
                        </button>
                        <button className="crm-btn crm-btn-danger crm-btn-sm" onClick={() => handleDelete(c.contactId)}>
                          Del
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}

      {/* Add / Edit Tab */}
      {tab === 'add' && (
        <div className="crm-form">
          <h3>{editingId ? 'Edit Contact' : 'Add New Contact'}</h3>
          <div className="crm-form-grid">
            <div className="crm-form-group">
              <label>Name</label>
              <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Full name" />
            </div>
            <div className="crm-form-group">
              <label>Company</label>
              <input value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} placeholder="Company name" />
            </div>
            <div className="crm-form-group">
              <label>Email</label>
              <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="email@example.com" />
            </div>
            <div className="crm-form-group">
              <label>Phone</label>
              <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="(555) 123-4567" />
            </div>
            <div className="crm-form-group">
              <label>Status</label>
              <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                {PIPELINE_COLUMNS.map(s => (
                  <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                ))}
              </select>
            </div>
            <div className="crm-form-group">
              <label>Source</label>
              <select value={form.source} onChange={e => setForm({ ...form, source: e.target.value })}>
                {SOURCES.map(s => (
                  <option key={s} value={s}>{s.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>
                ))}
              </select>
            </div>
            <div className="crm-form-group full-width">
              <label>Notes</label>
              <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Any notes about this prospect..." />
            </div>
          </div>
          <div className="crm-form-actions">
            <button className="crm-btn crm-btn-primary" onClick={handleSave}>
              {editingId ? 'Update Contact' : 'Add Contact'}
            </button>
            <button className="crm-btn crm-btn-secondary" onClick={() => { setTab('contacts'); setEditingId(null); setForm(emptyForm); }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Activity Log Tab */}
      {tab === 'activity' && (
        <div className="crm-activity-log">
          {!selectedContact ? (
            <div className="crm-empty">
              <p>Select a contact to view activity</p>
              <button className="crm-btn crm-btn-secondary" onClick={() => setTab('contacts')}>Go to Contacts</button>
            </div>
          ) : (
            <>
              <div className="crm-contact-detail">
                <h3>{selectedContact.name}</h3>
                <div className="detail-company">{selectedContact.company}</div>
                <div className="crm-contact-detail-grid">
                  <div className="crm-detail-field">
                    <label>Email</label>
                    <span>{selectedContact.email || '-'}</span>
                  </div>
                  <div className="crm-detail-field">
                    <label>Phone</label>
                    <span>{selectedContact.phone || '-'}</span>
                  </div>
                  <div className="crm-detail-field">
                    <label>Status</label>
                    <span className={`crm-status-badge ${selectedContact.status}`}>{STATUS_LABELS[selectedContact.status]}</span>
                  </div>
                  <div className="crm-detail-field">
                    <label>Source</label>
                    <span>{selectedContact.source}</span>
                  </div>
                </div>
                {selectedContact.notes && (
                  <div className="crm-detail-field" style={{ marginBottom: 16 }}>
                    <label>Notes</label>
                    <span>{selectedContact.notes}</span>
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="crm-btn crm-btn-secondary crm-btn-sm" onClick={() => startEdit(selectedContact)}>Edit</button>
                  <button className="crm-btn crm-btn-secondary crm-btn-sm" onClick={() => handleStatusChange(selectedContact.contactId, 'contacted')}>Mark Contacted</button>
                </div>
              </div>

              <h4 style={{ margin: '24px 0 12px' }}>Log Activity</h4>
              <div className="crm-activity-form">
                <select value={activityType} onChange={e => setActivityType(e.target.value as any)}>
                  <option value="call">Call</option>
                  <option value="email">Email</option>
                  <option value="demo">Demo</option>
                  <option value="note">Note</option>
                </select>
                <input
                  placeholder="What happened?"
                  value={activityDesc}
                  onChange={e => setActivityDesc(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleLogActivity(); }}
                />
                <button className="crm-btn crm-btn-primary" onClick={handleLogActivity}>Log</button>
              </div>

              <h4 style={{ margin: '16px 0 12px' }}>Activity History</h4>
              {(!selectedContact.activities || selectedContact.activities.length === 0) ? (
                <div className="crm-empty"><p>No activity logged yet</p></div>
              ) : (
                <ul className="crm-activity-list">
                  {[...selectedContact.activities].reverse().map(a => (
                    <li key={a.id} className="crm-activity-item">
                      <div className={`crm-activity-icon ${a.type}`}>{ACTIVITY_ICONS[a.type] || 'N'}</div>
                      <div className="crm-activity-content">
                        <div className="activity-desc">{a.description}</div>
                        <div className="activity-meta">
                          {a.type.charAt(0).toUpperCase() + a.type.slice(1)} - {formatDate(a.createdAt)} by {a.createdBy}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      )}

      {/* Stats Tab */}
      {tab === 'stats' && stats && (
        <div>
          <h3 style={{ margin: '0 0 16px', fontSize: 18, color: '#1e293b' }}>Pipeline Breakdown</h3>
          <table className="crm-contacts-table" style={{ maxWidth: 480 }}>
            <thead>
              <tr>
                <th>Stage</th>
                <th>Count</th>
              </tr>
            </thead>
            <tbody>
              {PIPELINE_COLUMNS.map(s => (
                <tr key={s}>
                  <td><span className={`crm-status-badge ${s}`}>{STATUS_LABELS[s]}</span></td>
                  <td>{stats.byStatus?.[s] || 0}</td>
                </tr>
              ))}
              <tr style={{ fontWeight: 600 }}>
                <td>Total</td>
                <td>{stats.total}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Social Media Tab */}
      {tab === 'social' && (
        <div className="crm-social-section">
          <div className="crm-social-subtabs">
            {([
              ['accounts', 'Accounts'],
              ['posts', 'Posts'],
              ['linkedin', 'LinkedIn'],
              ['facebook', 'Facebook'],
              ['google', 'Google'],
              ['offers', 'Offers'],
              ['reviews', 'Reviews'],
            ] as [SocialSubTab, string][]).map(([key, label]) => (
              <button
                key={key}
                className={`crm-social-subtab ${socialSubTab === key ? 'active' : ''}`}
                onClick={() => setSocialSubTab(key)}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="crm-social-content">
            {socialSubTab === 'accounts' && (
              <SocialMediaConfiguration storeId={BSO_STORE_ID} storeName={BSO_STORE_NAME} />
            )}
            {socialSubTab === 'posts' && (
              <SocialMediaPostManager storeId={BSO_STORE_ID} />
            )}
            {socialSubTab === 'linkedin' && (
              <LinkedInBusiness storeId={BSO_STORE_ID} storeName={BSO_STORE_NAME} />
            )}
            {socialSubTab === 'facebook' && (
              <FacebookOAuth storeId={BSO_STORE_ID} storeName={BSO_STORE_NAME} />
            )}
            {socialSubTab === 'google' && (
              <GoogleBusiness storeId={BSO_STORE_ID} storeName={BSO_STORE_NAME} />
            )}
            {socialSubTab === 'offers' && (
              <OfferDistribution storeId={BSO_STORE_ID} />
            )}
            {socialSubTab === 'reviews' && (
              <ReviewManagement storeId={BSO_STORE_ID} storeName={BSO_STORE_NAME} />
            )}
          </div>
        </div>
      )}

      {/* Content Hub Tab */}
      {tab === 'content' && (
        <ContentHub />
      )}

      {/* ABM Tab */}
      {tab === 'abm' && (
        <ABMDashboard />
      )}
    </div>
  );
};

export default CRMDashboard;
